import { useLayoutEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty, isNil, isNumber } from "@s-libs/micro-dash";
import api, { ApiCatalog } from "@/api";
import useUser from "@/users/useUser";
import {
  RosterClassroom,
  RosterClassroomResponse,
} from "@/api/catalogs/mockData/rosterClassroomMock";
import getUserProperties from "@/users/getUserProperties";
import { teacherQueries } from "@/teacher";
import { DistrictBulkProcessingConfigurationResponse } from "@/district";
import { userClassroomsQueries } from "../queries";

export type MutableRosterClassroom = RosterClassroom & { checked: boolean };
export type RosterClassroomMap = Record<string, MutableRosterClassroom>;

type SyncClassroomsPayload = {
  provider: string;
  classrooms: Pick<
    RosterClassroom,
    "id" | "grade" | "subject_slugs" | "school"
  >[];
};

export type RosterClassroomSyncResponse = {
  expected_classroom_count: number;
  has_failures: boolean;
  id: number;
  provider: string;
  time_completed: string | null;
  time_started: string | null;
};

export const isRosterClassroomValid = (classroom: MutableRosterClassroom) => {
  const isFieldsValid =
    classroom.checked &&
    isNumber(classroom.grade) &&
    !isEmpty(classroom.subject_slugs) &&
    !isEmpty(classroom.school);
  return classroom.is_synced || isFieldsValid;
};

export const isRosterClassroomFullySynced = ({
  is_synced: isSynced,
  grade,
  subject_slugs: subjectSlugs,
  school,
}: RosterClassroom) => {
  return isSynced && !isNil(grade) && !isEmpty(subjectSlugs) && !isNil(school);
};
export const syncRosterClassroomsMutationKey = ["syncRosterClassrooms"];

const useClassroomSync = () => {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const { hasBulkClassrooms } = getUserProperties(user, {
    shouldUseNewUserApi: false,
  });
  const {
    data: rosterClassrooms,
    isLoading: isLoadingRosterClassrooms,
    ...rosterClassroomsRest
  } = useQuery({
    queryKey: [
      ApiCatalog.rosterClassroom.key,
      {
        provider: user.auth_type,
      },
    ],
    queryFn: () =>
      api().get<RosterClassroomResponse>(ApiCatalog.rosterClassroom.endpoint, {
        queryParams: {
          provider: user.auth_type,
        },
      }),
    // We need to always fetch because the classroom list takes time to update in DB
    refetchOnMount: "always",
  });
  const { data: userSchools, ...userSchoolsRest } = useQuery(
    teacherQueries.schools(),
  );
  const { data: classrooms, ...classroomsRest } = useQuery({
    ...userClassroomsQueries.filter({
      page_size: 50,
      sort: "name",
      hide_archived: false,
    }),
    select: (data) => data.results,
  });
  const {
    data: districtBulkProcessingConfiguration,
    ...districtBulkProcessingConfigurationRest
  } = useQuery({
    queryKey: [ApiCatalog.districtBulkProcessingConfiguration.key],
    queryFn: () =>
      api().get<DistrictBulkProcessingConfigurationResponse>(
        ApiCatalog.districtBulkProcessingConfiguration.endpoint,
      ),
    enabled: hasBulkClassrooms,
  });
  const {
    mutateAsync: requestSyncRosterClassrooms,
    ...mutateSyncRosterClassroomsRest
  } = useMutation({
    mutationKey: syncRosterClassroomsMutationKey,
    mutationFn: async (payload: SyncClassroomsPayload) =>
      api().post<RosterClassroomSyncResponse>("/api/v2/roster-sync/", payload),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [
          ApiCatalog.rosterClassroom.key,
          {
            provider: user.auth_type,
          },
        ],
      }),
  });
  const { mutateAsync: requestDeleteClassroom, ...mutateDeleteClassroomRest } =
    useMutation({
      mutationFn: async (classroomId: number) =>
        api().delete(`/api/v3/classroom/${classroomId}`),
    });

  const [selectedRosterClassrooms, setSelectedRosterClassrooms] =
    useState<RosterClassroomMap>({});
  const mainRosterProvider =
    districtBulkProcessingConfiguration?.[0]?.roster_provider;
  const activeBulkSyncedClassrooms = classrooms?.filter(
    ({ date_archived, provider }) =>
      !date_archived &&
      provider.is_bulk &&
      provider.internal_name === mainRosterProvider,
  );

  const isAllRosterClassroomsAlreadyFullySynced = useMemo(
    () =>
      Boolean(rosterClassrooms?.length) &&
      Boolean(rosterClassrooms?.every(isRosterClassroomFullySynced)),
    [rosterClassrooms],
  );
  const hasSelectedAnyClassroomForSyncing = !isEmpty(selectedRosterClassrooms);
  const hasSelectedNewClassroomForSyncing = useMemo(
    () =>
      Object.values(selectedRosterClassrooms!).some(
        (rosterClassroom) => !isRosterClassroomFullySynced(rosterClassroom),
      ),
    [selectedRosterClassrooms],
  );
  const isUserReSyncing =
    hasSelectedAnyClassroomForSyncing &&
    Object.values(selectedRosterClassrooms!).some(isRosterClassroomFullySynced);
  const isFormValid = useMemo(
    () =>
      hasSelectedAnyClassroomForSyncing &&
      Object.values(selectedRosterClassrooms || {}).every(
        isRosterClassroomValid,
      ),
    [selectedRosterClassrooms],
  );

  const archiveAllActiveBulkClassrooms = async () => {
    return Promise.all(
      (activeBulkSyncedClassrooms || []).map(({ id }) =>
        requestDeleteClassroom(id),
      ),
    );
  };

  const syncRosterClassrooms = async () => {
    if (isAllRosterClassroomsAlreadyFullySynced) return;
    if (!isFormValid) {
      throw new Error("invalid form");
    }
    // We need to send always all selected classrooms
    // if we filter out synced classrooms, they will be archived
    const syncRosterClassroomPayload = Object.values(
      selectedRosterClassrooms || {},
    ).reduce<SyncClassroomsPayload>(
      (previousValue, currentValue) => ({
        provider: previousValue.provider,
        classrooms: [
          ...previousValue.classrooms,
          {
            id: currentValue.id,
            grade: currentValue.grade,
            school: currentValue.school,
            subject_slugs: currentValue.subject_slugs,
          },
        ],
      }),
      {
        provider: user.auth_type,
        classrooms: [],
      },
    );
    if (isEmpty(syncRosterClassroomPayload.classrooms)) return;
    return requestSyncRosterClassrooms(syncRosterClassroomPayload);
  };

  const onClassroomSelection = (selectedClassroom: MutableRosterClassroom) => {
    if (selectedClassroom.checked) {
      setSelectedRosterClassrooms((prevState) => ({
        ...prevState,
        [selectedClassroom.id]: selectedClassroom,
      }));
    } else {
      setSelectedRosterClassrooms((prevState) => {
        const { [selectedClassroom.id]: _, ...remainingState } =
          prevState ?? {};
        return remainingState;
      });
    }
  };

  useLayoutEffect(() => {
    const selection =
      rosterClassrooms?.reduce(
        (current, classroom) =>
          classroom.is_synced
            ? { ...current, [classroom.id]: { ...classroom, checked: true } }
            : current,
        {},
      ) || {};
    setSelectedRosterClassrooms(selection);
    return () => {
      setSelectedRosterClassrooms({});
    };
  }, [rosterClassrooms]);

  return {
    rosterClassrooms: {
      data: rosterClassrooms,
      isLoading: isLoadingRosterClassrooms,
      ...rosterClassroomsRest,
    },
    classrooms: {
      data: classrooms,
      ...classroomsRest,
    },
    userSchools: {
      data: userSchools,
      ...userSchoolsRest,
    },
    districtBulkProcessingConfiguration: {
      data: districtBulkProcessingConfiguration,
      ...districtBulkProcessingConfigurationRest,
    },
    mutateDeleteClassroomStates: mutateDeleteClassroomRest,
    mutateSyncRosterClassroomsStates: mutateSyncRosterClassroomsRest,
    selectedRosterClassrooms,
    archiveAllActiveBulkClassrooms,
    activeBulkSyncedClassrooms,
    onClassroomSelection,
    syncRosterClassrooms,
    isAllRosterClassroomsAlreadyFullySynced,
    isUserReSyncing,
    hasSelectedAnyClassroomForSyncing,
    isFormValid,
    mainRosterProvider,
    hasSelectedNewClassroomForSyncing,
  };
};

export default useClassroomSync;
